import React, { useState, useEffect } from 'react';
import { Download, Smartphone, Info, X } from 'lucide-react';
import { usePWAInstall } from './usePWAInstall';
import { trackUserInteraction } from '../services/analytics';

interface PWAInstallButtonProps {
  primaryColor?: string;
  className?: string;
  serviceKey?: string;
  serviceName?: string;
}

const PWAInstallButton: React.FC<PWAInstallButtonProps> = ({ 
  primaryColor = '#007bff', 
  className = '',
  serviceKey = '', 
  serviceName = 'mythos365 Service' 
}) => {
  const { isInstallable, isInstalled, installPWA, getInstallInstructions } = usePWAInstall({ serviceKey });
  const [showInstructions, setShowInstructions] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isPWAInstallable, setIsPWAInstallable] = useState(false);
  const [isPWAInstalled, setIsPWAInstalled] = useState(false);  useEffect(() => {
    // Create service-specific manifest
    const updateManifest = () => {
      // Remove existing manifest link
      const existingManifest = document.querySelector('link[rel="manifest"]');
      if (existingManifest) {
        existingManifest.remove();
      }

      // Get the base URL for absolute paths
      const baseUrl = window.location.origin;

      // Create service-specific manifest
      const manifest = {
        name: `${serviceName} - mythos365`,
        short_name: serviceName || 'mythos365',
        description: `Professional service booking platform - ${serviceName}`,
        start_url: serviceKey && serviceKey !== 'default' 
          ? `${baseUrl}/${serviceKey}?pwa_launch=true` 
          : `${baseUrl}/?pwa_launch=true`,
        display: 'standalone',
        background_color: '#ffffff',
        theme_color: primaryColor,
        orientation: 'portrait',
        scope: `${baseUrl}/`,
        categories: ['business', 'productivity', 'utilities'],
        lang: 'en',
        dir: 'ltr',
        icons: [
          {
            src: `${baseUrl}/book-icon.svg`,
            sizes: 'any',
            type: 'image/svg+xml',
            purpose: 'any maskable'
          },
          {
            src: `${baseUrl}/favicon.ico`,
            sizes: '48x48',
            type: 'image/x-icon'
          }
        ]
      };

      // Create blob URL for the manifest
      const manifestBlob = new Blob([JSON.stringify(manifest)], { type: 'application/json' });
      const manifestUrl = URL.createObjectURL(manifestBlob);

      // Add new manifest link
      const link = document.createElement('link');
      link.rel = 'manifest';
      link.href = manifestUrl;
      document.head.appendChild(link);

      console.log('PWA: Updated manifest for service:', serviceKey, 'Start URL:', manifest.start_url);
    };

    updateManifest();

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsPWAInstallable(true);
    };

    // Check if app is already installed
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isIOSStandalone = (window.navigator as any).standalone === true;
    setIsPWAInstalled(isStandalone || isIOSStandalone);

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [serviceKey, serviceName, primaryColor]);

  const handleInstallPWA = async () => {
    if (!deferredPrompt) return;

    try {
      // Store the service key in localStorage before installation
      if (serviceKey && serviceKey !== 'default') {
        localStorage.setItem('pwa_install_service_key', serviceKey);
        localStorage.setItem('pwa_install_service_name', serviceName || serviceKey);
        localStorage.setItem('pwa_install_service_url', `/${serviceKey}`);
      }
      
      // Show the install prompt
      deferredPrompt.prompt();
      
      // Wait for the user to respond to the prompt
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('PWA installation accepted for service:', serviceKey);
        setIsPWAInstalled(true);
        trackUserInteraction('PWA Install Success', { 
          serviceKey, 
          serviceName,
          startUrl: serviceKey ? `/${serviceKey}` : '/'
        });
      } else {
        trackUserInteraction('PWA Install Declined', { serviceKey, serviceName });
      }
      
      // Clear the deferredPrompt
      setDeferredPrompt(null);
      setIsPWAInstallable(false);
    } catch (error) {
      console.error('Error during PWA installation:', error);
      trackUserInteraction('PWA Install Error', { serviceKey, error: error.message });
    }
  };
  const handleInstall = async () => {
    const instructions = getInstallInstructions();
    
    if (instructions && (navigator.userAgent.toLowerCase().includes('safari') && !navigator.userAgent.toLowerCase().includes('chrome'))) {
      // For iOS Safari, show instructions
      setShowInstructions(true);
      trackUserInteraction('PWA Install Instructions Shown', { platform: instructions.platform });
    } else if (isPWAInstallable) {
      // Use the direct install prompt
      await handleInstallPWA();
    } else {
      // For other browsers without prompt, show instructions
      setShowInstructions(true);
      trackUserInteraction('PWA Install Instructions Shown', { platform: 'Manual' });
    }
  };

  const instructions = getInstallInstructions();
  if (isInstalled || isPWAInstalled) {
    return null;
  }

  const isCompact = className.includes('compact');

  return (
    <>
      {isCompact ? (
        <div className="flex flex-col items-center space-y-2 w-full max-w-48">
          {isPWAInstallable ? (
            <button 
              onClick={handleInstallPWA}
              className="inline-flex items-center px-3 py-1.5 border border-blue-300 text-xs font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors"
            >
              <Download className="w-3 h-3 mr-1.5" />
              Install App
            </button>
          ) : (
            <div className="text-center">
              <p className="text-xs text-gray-500 mb-1">📱 Install as app</p>
              <p className="text-xs text-gray-400 leading-tight">
                Use browser's "Add to Home Screen" option
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="pwa-install-container bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 mt-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <Smartphone className="h-6 w-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-medium text-gray-900">
                📱 Install as App
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Install {serviceName} as a mobile or desktop app for quick access!
              </p>
              
              {isPWAInstallable ? (
                <button 
                  onClick={handleInstallPWA}
                  className="mt-3 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Install App
                </button>
              ) : (
                <div className="mt-3">
                  <p className="text-xs text-gray-500">
                    Use your browser's "Add to Home Screen" or "Install" option to install this app.
                  </p>
                  <div className="text-xs text-gray-400 mt-1">
                    • <strong>Chrome/Edge:</strong> Click the install icon in the address bar
                    <br />
                    • <strong>Safari (iOS):</strong> Tap Share → Add to Home Screen
                    <br />
                    • <strong>Firefox:</strong> Menu → Install
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Instructions Modal for iOS Safari */}
      {showInstructions && instructions && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-4 border-b">
              <div className="flex items-center">
                <Info className="w-5 h-5 mr-2 text-blue-500" />
                <h3 className="text-lg font-semibold">Install App</h3>
              </div>
              <button
                onClick={() => setShowInstructions(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4">
              <p className="text-gray-600 mb-4">
                To install this app on your {instructions.platform}:
              </p>
              
              <ol className="space-y-2 text-sm text-gray-700">
                {instructions.instructions.map((step, index) => (
                  <li key={index} className="flex items-start">
                    <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-medium mr-3 mt-0.5">
                      {index + 1}
                    </span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
              
              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-700">
                  💡 Once installed, you can access the app directly from your home screen!
                </p>
              </div>
            </div>
            
            <div className="p-4 border-t">
              <button
                onClick={() => setShowInstructions(false)}
                className="w-full px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PWAInstallButton;